import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './DeliveryModal.css';
import { 
  trackParcel,
  selectParcelTrackingState 
} from '../../redux/WithcookieSlice';
import { useTranslation } from '../../redux/TranslationHelper';

const DeliveryModal = ({ isOpen, onClose, checkout }) => {
  const dispatch = useDispatch();
  const parcelTrackingState = useSelector(selectParcelTrackingState);
  const withcookieData = useSelector((state) => state.withcookie?.withcookieData);
  const { t } = useTranslation();
  
  const [trackingData, setTrackingData] = useState(null);
  const [selectedParcelIndex, setSelectedParcelIndex] = useState(0);

  // 테마 색상 설정
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#3b82f6";
  const secondaryColor = colorSet?.secondary || "#2563eb";
  const textColor = colorSet?.text || "#ffffff";
  const backgroundColor = colorSet?.background || "#ffffff";

  // 배송 상태 매핑
  const getStatusDisplay = (status) => {
    const statusMap = {
      'PREPARING': { text: t('상품 준비 중'), class: 'status-preparing', icon: '📦' },
      'SHIPPING': { text: t('배송 중'), class: 'status-shipping', icon: '🚚' },
      'ARRIVED': { text: t('배송 완료'), class: 'status-completed', icon: '✅' },
      'CANCELED': { text: t('주문 취소됨'), class: 'status-cancelled', icon: '❌' },
      'ISSUE': { text: t('배송 문제'), class: 'status-issue', icon: '⚠️' }
    };
    return statusMap[status] || { text: status, class: 'status-default', icon: '📋' };
  };

  // 개별 배송 추적
  const handleTrackParcel = useCallback(async (parcel) => {
    if (parcel.parcelCompanyCode && parcel.parcelCode) {
      console.log('[DeliveryModal] Tracking parcel:', parcel);
      const result = await dispatch(trackParcel({
        carrierId: parcel.parcelCompanyCode,
        trackingNumber: parcel.parcelCode
      }));
      
      console.log('[DeliveryModal] Track result:', result);
      
      if (result.payload?.tracking) {
        setTrackingData(result.payload.tracking);
      } else {
        console.error('[DeliveryModal] No tracking data in result:', result);
      }
    } else {
      console.error('[DeliveryModal] Missing parcel data:', parcel);
    }
  }, [dispatch]);

  // 모달이 열릴 때 첫 번째 배송 자동 추적
  useEffect(() => {
    if (isOpen && checkout?.id) {
      console.log('[DeliveryModal] Opening modal for checkout:', checkout.id);
      console.log('[DeliveryModal] Checkout data:', checkout);
      
      // 첫 번째 배송 정보가 있으면 자동으로 추적 조회
      const parcels = checkout?.parcels || [];
      if (parcels.length > 0) {
        const firstParcel = parcels[0];
        if (firstParcel.parcelCompanyCode && firstParcel.parcelCode) {
          console.log('[DeliveryModal] Auto-tracking first parcel:', firstParcel);
          handleTrackParcel(firstParcel);
        }
      }
    }
  }, [isOpen, checkout?.id, checkout, handleTrackParcel]);

  // 모달 닫기 핸들러
  const handleClose = () => {
    setTrackingData(null);
    setSelectedParcelIndex(0);
    onClose();
  };

  // 동적 스타일 설정
  useEffect(() => {
    if (isOpen) {
      const root = document.documentElement;
      root.style.setProperty('--delivery-modal-primary', primaryColor);
      root.style.setProperty('--delivery-modal-secondary', secondaryColor);
      root.style.setProperty('--delivery-modal-text', textColor);
      root.style.setProperty('--delivery-modal-background', backgroundColor);
    }
  }, [isOpen, primaryColor, secondaryColor, textColor, backgroundColor]);

  // 디버깅을 위한 로그
  useEffect(() => {
    if (isOpen) {
      console.log('[DeliveryModal] parcelTrackingState:', parcelTrackingState);
      console.log('[DeliveryModal] trackingData:', trackingData);
    }
  }, [isOpen, parcelTrackingState, trackingData]);

  if (!isOpen) return null;

  const parcels = checkout?.parcels || [];

  return (
    <div className="delivery-modal-overlay" onClick={handleClose}>
      <div className="delivery-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="delivery-modal-header">
          <h2>{t('배송 조회')}</h2>
          <button className="delivery-modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="delivery-modal-body">
          {/* 주문 요약 정보 */}
          <div className="order-summary-card">
            <div className="summary-header">
              <h3>{t('주문')} #{checkout?.id}</h3>
              <span className={`status-badge ${getStatusDisplay(checkout?.status).class}`}>
                {getStatusDisplay(checkout?.status).icon} {getStatusDisplay(checkout?.status).text}
              </span>
            </div>
            <div className="summary-details">
              <div className="detail-item">
                <span className="label">{t('수령인')}</span>
                <span className="value">{checkout?.receiver_name}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t('연락처')}</span>
                <span className="value">{checkout?.receiver_phone}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t('주문금액')}</span>
                <span className="value">{Number(checkout?.total_price || 0).toLocaleString()}원</span>
              </div>
            </div>
          </div>

          {/* 배송 정보 및 추적 */}
          {parcels.length > 0 ? (
            <div className="delivery-section">
              {/* 배송 정보 탭 */}
              {parcels.length > 1 && (
                <div className="parcel-tabs">
                  {parcels.map((parcel, index) => (
                    <button
                      key={parcel.id || index}
                      className={`parcel-tab ${selectedParcelIndex === index ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedParcelIndex(index);
                        handleTrackParcel(parcel);
                      }}
                    >
                      {t('배송')} {index + 1}
                    </button>
                  ))}
                </div>
              )}

              {/* 현재 선택된 배송 정보 */}
              {parcels[selectedParcelIndex] && (
                <div className="delivery-info-card">
                  <div className="delivery-header">
                    <h4>{t('배송 정보')}</h4>
                    <button 
                      className="refresh-button"
                      onClick={() => handleTrackParcel(parcels[selectedParcelIndex])}
                      disabled={parcelTrackingState.loading}
                    >
                      {parcelTrackingState.loading ? '⟳' : '🔄'} {t('새로고침')}
                    </button>
                  </div>
                  
                  <div className="delivery-details">
                    <div className="detail-row">
                      <span className="label">{t('택배사')}</span>
                      <span className="value">{parcels[selectedParcelIndex].parcelCompanyName || t('정보 없음')}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('송장번호')}</span>
                      <span className="value tracking-number">{parcels[selectedParcelIndex].parcelCode || t('정보 없음')}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('배송상태')}</span>
                      <span className={`status-badge ${getStatusDisplay(parcels[selectedParcelIndex].status).class}`}>
                        {getStatusDisplay(parcels[selectedParcelIndex].status).icon} {getStatusDisplay(parcels[selectedParcelIndex].status).text}
                      </span>
                    </div>

                  </div>
                </div>
              )}

              {/* 배송 추적 정보 */}
              {trackingData && (
                <div className="tracking-info-card">
                  <h4>{t('배송 추적')}</h4>
                  
                  {trackingData.error ? (
                    <div className="tracking-error">
                      <div className="error-icon">⚠️</div>
                      <p>{trackingData.error}</p>
                    </div>
                  ) : (
                    <>
                      {/* 현재 상태 */}
                      {trackingData.lastEvent && (
                        <div className="current-status-card">
                          <div className="current-status-header">
                            <span className="status-icon">📍</span>
                            <div>
                              <div className="status-title">{trackingData.lastEvent.status?.name}</div>
                              <div className="status-time">
                                {new Date(trackingData.lastEvent.time).toLocaleString('ko-KR')}
                              </div>
                            </div>
                          </div>
                          <div className="status-description">{trackingData.lastEvent.description}</div>
                        </div>
                      )}

                      {/* 배송 이력 */}
                      {trackingData.events && trackingData.events.length > 0 && (
                        <div className="tracking-timeline">
                          <h5>{t('배송 이력')}</h5>
                          <div className="timeline">
                            {trackingData.events.map((event, index) => (
                              <div key={index} className="timeline-item">
                                <div className="timeline-marker"></div>
                                <div className="timeline-content">
                                  <div className="event-header">
                                    <span className="event-status">{event.status?.name}</span>
                                    <span className="event-time">
                                      {new Date(event.time).toLocaleString('ko-KR')}
                                    </span>
                                  </div>
                                  <div className="event-description">{event.description}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* 로딩 상태 */}
              {parcelTrackingState.loading && (
                <div className="loading-card">
                  <div className="loading-spinner">⏳</div>
                  <p>{t('배송 정보를 조회하고 있습니다...')}</p>
                </div>
              )}

              {/* 에러 상태 */}
              {parcelTrackingState.error && (
                <div className="error-card">
                  <div className="error-icon">❌</div>
                  <p>{parcelTrackingState.error}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="no-delivery-card">
              <div className="no-delivery-icon">📦</div>
              <h4>{t('배송 정보 없음')}</h4>
              <p>{t('아직 배송 정보가 등록되지 않았습니다.')}</p>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

export default DeliveryModal;